import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";
import { getWorldYRotation, orientedXZPenetration } from "../../utils/geometry.js";
import { CUSHION_LIKE_COMPONENT_TYPES, RESEATABLE_COMPONENT_TYPES } from "../../utils/constants.js";
import { _Y_AXIS } from "./shared.js";
import { COMPONENT_LENGTH_AXIS } from "../../furniture/AttachmentPoint.js";

const _scratchQuat = new THREE.Quaternion(); // Scratch quaternion to avoid per-frame allocation
const _scratchAxis = new THREE.Vector3();
const _dropQuat = new THREE.Quaternion();

// Max XZ distance from parent bounding box to keep an AP target while sliding
const AP_PERSISTENCE_DISTANCE = 1.0;

// Shrinks the ghost bounding box on each side before the component collision test.
// Cross-parent: generous so adjacent cushions/backrests on neighbouring modules can touch.
// Same-parent siblings: tight — two backrests on one sofa should never visibly overlap.
const COMPONENT_COLLISION_TOLERANCE = 0.04;
const COMPONENT_COLLISION_TOLERANCE_SAME_PARENT = 0.005;
// Upholstery gives: one cushion may press this far into another before it counts as a collision.
const CUSHION_PRESS_TOLERANCE = 0.02;
// Spacing (m) a ranged AP is sampled at for free runs — Unity's _cushionAnchorInterval.
const RANGE_SAMPLE_STEP = 0.01;
// How far (m) a piece on a range may overhang either end; lets cascais-pc sit centred on an nc.
const RANGE_OVERHANG = 0.25;
// Rise (m) above a ranged piece's underside a seat's give may reach; an armrest or neighbour's back won't.
const SUPPORT_RISE_TOLERANCE = 0.08;
// End probes sit this far (m) inside the box, which upholstery pressing into the next piece may cover.
const SUPPORT_END_INSET = 0.06;

function collectSubtree(furniture) {
  const set = new Set();
  const stack = [furniture];
  while (stack.length > 0) {
    const f = stack.pop();
    set.add(f);
    if (f.attachedChildren?.length) stack.push(...f.attachedChildren);
  }
  return set;
}

const nestlesWith = (ap, urlCode) => !!(ap?.fixed && urlCode && ap.requiresFlankingCushions?.includes(urlCode));

// Components only ever yaw, so a plan-view oriented box plus a Y interval is exact.
const _obbA = { cx: 0, cz: 0, cy: 0, cos: 1, sin: 0, hx: 0, hz: 0, hh: 0 };
const _obbB = { cx: 0, cz: 0, cy: 0, cos: 1, sin: 0, hx: 0, hz: 0, hh: 0 };
const _obbPos = new THREE.Vector3();
const _obbCenter = new THREE.Vector3();
const _obbScale = new THREE.Vector3();
const _obbQuat = new THREE.Quaternion();
const _obbEuler = new THREE.Euler();
const _moveMatrix = new THREE.Matrix4();
const _pieceMatrix = new THREE.Matrix4();
const _runAncestors = new Set();
const _runSlide = new THREE.Vector3();
const _runPos = new THREE.Vector3();
const _runQuat = new THREE.Quaternion();
const _runScale = new THREE.Vector3();
const _runOffset = new THREE.Vector3();
const _runAxis = new THREE.Vector3();
const _runAxisQuat = new THREE.Quaternion();
const _boxAxis = new THREE.Vector3();

const _moduleMeshes = [];
const _moduleOwners = [];
const _moduleInverses = [];
const _seatChain = new Set();
const _moduleBvhs = new WeakMap();
const _probe = new THREE.Vector3();
const _probeRay = new THREE.Ray();
const _localRay = new THREE.Ray();
const _DOWN = new THREE.Vector3(0, -1, 0);
const SUPPORT_PROBES = [0, -1, 1];

// Our own tree, not geometry.boundsTree: the path tracer builds that one with its own options.
function meshBvh(geometry) {
  let bvh = _moduleBvhs.get(geometry);
  if (!bvh) {
    bvh = new MeshBVH(geometry);
    _moduleBvhs.set(geometry, bvh);
  }
  return bvh;
}

export const freeRun = (min, max) => ({ min, max, mid: (min + max) / 2 });

/** `t` moved into the nearest free run; into the range alone when the runs aren't known. */
export function clampToRuns(t, runs, range) {
  if (!runs?.length) return THREE.MathUtils.clamp(t, range.min, range.max);
  let best = t, bestDist = Infinity;
  for (const run of runs) {
    const c = THREE.MathUtils.clamp(t, run.min, run.max);
    const d = Math.abs(c - t);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return best;
}

// `box` is mesh-local {localCenterOffset, size}, placed by the mesh's world matrix.
function setComponentObb(out, matrixWorld, box) {
  matrixWorld.decompose(_obbPos, _obbQuat, _obbScale);
  const yaw = _obbEuler.setFromQuaternion(_obbQuat, 'YXZ').y;
  _obbCenter.copy(box.localCenterOffset).multiply(_obbScale).applyQuaternion(_obbQuat).add(_obbPos);
  out.cx = _obbCenter.x;
  out.cy = _obbCenter.y;
  out.cz = _obbCenter.z;
  out.cos = Math.cos(yaw);
  out.sin = Math.sin(yaw);
  out.hx = box.size.x * Math.abs(_obbScale.x) / 2;
  out.hh = box.size.y * Math.abs(_obbScale.y) / 2;
  out.hz = box.size.z * Math.abs(_obbScale.z) / 2;
}

// Overlapping only when the penetration exceeds `t` on Y and every XZ separating axis.
function componentObbsOverlap(a, b, t) {
  return a.hh + b.hh - Math.abs(a.cy - b.cy) > t && orientedXZPenetration(a, b) > t;
}

export const componentDragMethods = {
  /** Restore a component to its pre-drag AP and transform. */
  restoreComponentToOriginal(furniture) {
    furniture.getMesh().visible = true;
    furniture.getMesh().position.copy(this.componentDragOriginalPos);
    furniture.getMesh().quaternion.copy(this.componentDragOriginalQuat);
    const ap = this.componentDragOriginalAP;
    if (ap && !ap.isOccupied) {
      furniture.attachTo(ap);
      if (this.componentDragOriginalLocalPos) ap.setSlide(furniture, this.componentDragOriginalLocalPos);
    }
    furniture.calculateBoundingBox();
  },

  // AP whose plus is under the cursor, else the nearest; past range, keep the current one near its parent.
  _findComponentSnapTarget(cursorPos, furnitureData, exclude = null) {
    const found = this.findClosestComponentAttachment(cursorPos, furnitureData, exclude, this.raycaster?.ray);
    if (found) return found;
    const prev = this.componentDragTarget;
    const full = prev?.range && this.componentFreeRuns(prev, furnitureData, exclude)?.length === 0;
    if (prev && !prev.isOccupied && !full && this._isCursorNearParentBB(prev, cursorPos) &&
        prev.parentFurniture.shouldShowAttachmentPoint(prev)) {
      return prev;
    }
    return null;
  },

  // A re-dragged cushion or backrest may re-seat on any free compatible AP (a backrest takes
  // its cushions along); a panel stays on its own.
  _resolveComponentDragTarget(furniture, cursorPos) {
    const original = this.componentDragOriginalAP;
    if (!RESEATABLE_COMPONENT_TYPES.has(furniture.furnitureData?.componentType)) return original;
    return this._findComponentSnapTarget(cursorPos, furniture.furnitureData, this.componentDragSubtree) ?? original;
  },

  /**
   * Check if cursor is within AP_PERSISTENCE_DISTANCE of an AP's parent bounding box (XZ only).
   * Used to keep the current snap target while the cursor slides beyond SNAP_DISTANCE.
   */
  _isCursorNearParentBB(ap, cursorPos) {
    const bb = ap.parentFurniture.boundingBox;
    if (!bb) return false;
    const xDist = Math.max(bb.min.x - cursorPos.x, 0, cursorPos.x - bb.max.x);
    const zDist = Math.max(bb.min.z - cursorPos.z, 0, cursorPos.z - bb.max.z);
    return Math.sqrt(xDist * xDist + zDist * zDist) < AP_PERSISTENCE_DISTANCE;
  },

  /** Snapped pose of a component on `ap` into _componentSnapPos / _componentSnapQuat; returns the local slide or null. */
  _resolveComponentSnapPose(ap, furnitureData) {
    // The slide's cursor plane is read at the AP's own height.
    ap.getWorldPositionWithOffset(this._resolveConnectionOffset(ap, furnitureData), this._componentSnapPos);
    const slideLocal = this.computeComponentSlideOffset(ap, furnitureData);
    this._componentPoseAt(ap, furnitureData, slideLocal, this._componentSnapPos, this._componentSnapQuat);
    return slideLocal;
  },

  /** @see AttachmentPoint#resolveConnectionOffset */
  _resolveConnectionOffset(ap, furnitureData) {
    return ap
      ? ap.resolveConnectionOffset(furnitureData)
      : furnitureData?.properties?.connectionOffset ?? null;
  },

  /**
   * Compute a slide offset along an AP's side based on cursor world position.
   * Returns the result via the pre-allocated _slideOffset vector (caller must
   * consume before the next call), or null if projection fails.
   */
  computeComponentSlideOffset(ap, furnitureData = null) {
    // Fixed APs (e.g. diagon angled pillows) pin the component to the AP origin
    // — it never slides along the parent face, regardless of cursor position.
    if (ap.fixed) {
      return this._slideOffset.copy(ap.localPosition);
    }
    if (ap.range) return this._rangedSlideOffset(ap, furnitureData);

    const parentMesh = ap.parentFurniture.getMesh();
    const parentBB = ap.parentFurniture.boundingBox;
    if (!parentBB) return null;

    // Project cursor onto horizontal plane at AP height
    this._slidingPlane.set(this._slidingPlaneNormal, -this._componentSnapPos.y);
    const hit = this.raycaster.ray.intersectPlane(this._slidingPlane, this._slidingIntersection);
    if (!hit) return null;

    parentMesh.updateMatrixWorld(true);
    this._slidingLocalCursor.copy(this._slidingIntersection);
    parentMesh.worldToLocal(this._slidingLocalCursor);

    // Determine the module's LOCAL-SPACE extents for slide clamping.
    //
    // parentBB.min/max cannot be used as world-space AABB corners for authored
    // collision boxes: the authored path stores local size centered at the mesh
    // world position WITHOUT applying rotation, so min/max are wrong for any
    // rotated module. Converting those corners back via worldToLocal transposes
    // x/z, making the slide bounds wrong on rotated pieces.
    //
    // Fix: read local extents directly.
    //   • Authored collisionBox  → size/offset already live in local space.
    //   • Computed bounding box  → parentBB IS the true world AABB; un-envelope
    //     it back to local space. Modules only rotate in 90° steps so we just
    //     check sin vs cos and swap when |sin| > |cos|.
    let localSizeX, localSizeZ, localCtrX, localCtrZ;
    const authoredBox = ap.parentFurniture.furnitureData?.properties?.collisionBox;
    if (authoredBox?.size) {
      localSizeX = authoredBox.size.x;
      localSizeZ = authoredBox.size.z;
      localCtrX  = authoredBox.offset?.x ?? 0;
      localCtrZ  = authoredBox.offset?.z ?? 0;
    } else {
      // Computed-BB path: parentBB is the true world AABB.
      const wSizeX = parentBB.max.x - parentBB.min.x;
      const wSizeZ = parentBB.max.z - parentBB.min.z;
      const rotY = getWorldYRotation(parentMesh);
      const swapped = Math.abs(Math.sin(rotY)) > Math.abs(Math.cos(rotY));
      localSizeX = swapped ? wSizeZ : wSizeX;
      localSizeZ = swapped ? wSizeX : wSizeZ;
      localCtrX  = 0;
      localCtrZ  = 0;
    }

    const m = 1.2; // bounds multiplier
    const offset = this._slideOffset.copy(ap.localPosition);
    const side = ap.side;
    const lc = this._slidingLocalCursor;

    if (ap.facingAngle != null) {
      // Angled face (matacao3 SG curve backrests): slide along the face tangent
      // (perpendicular to the AP normal) instead of a cardinal axis, keeping the
      // panel's distance from centre along the normal fixed. The cardinal
      // branches below are the axis-aligned special cases of this same math.
      const theta = ap.facingAngle * Math.PI / 180;
      const nx = Math.sin(theta), nz = Math.cos(theta);   // outward normal (local XZ)
      const tx = Math.cos(theta), tz = -Math.sin(theta);  // along-face tangent
      const nComp   = ap.localPosition.x * nx + ap.localPosition.z * nz; // fixed
      const centerT = localCtrX * tx + localCtrZ * tz;
      const halfLenT = (Math.abs(tx) * localSizeX + Math.abs(tz) * localSizeZ) / 2;
      const cursorT = lc.x * tx + lc.z * tz;
      const tClamped = THREE.MathUtils.clamp(cursorT, centerT - halfLenT * m, centerT + halfLenT * m);
      offset.x = nComp * nx + tClamped * tx;
      offset.z = nComp * nz + tClamped * tz;
      return offset;
    }

    let slideX = false, slideZ = false;
    if (side === 'left' || side === 'right') {
      slideZ = true;
    } else if (side === 'front' || side === 'back') {
      slideX = true;
    } else if (side === 'top' && ap.componentRotationY === undefined
      && ap.parentFurniture.furnitureData?.componentType !== 'module') {
      slideX = slideZ = true;
    } else if (side === 'top' || side === 'inherit') {
      // Slide along the seated component's own length, at the yaw it rests at on this AP.
      const along = _scratchAxis.copy(COMPONENT_LENGTH_AXIS)
        .applyAxisAngle(_Y_AXIS, ap.componentRotationY ?? ap.localRotationY);
      if (Math.abs(along.x) > Math.abs(along.z)) slideX = true;
      else slideZ = true;
    }
    if (slideX) offset.x = THREE.MathUtils.clamp(lc.x, localCtrX - localSizeX / 2 * m, localCtrX + localSizeX / 2 * m);
    if (slideZ) offset.z = THREE.MathUtils.clamp(lc.z, localCtrZ - localSizeZ / 2 * m, localCtrZ + localSizeZ / 2 * m);

    return offset;
  },

  /** On a ranged AP: follow the cursor, clamped into a free run; over a plus, land on that run's middle. */
  _rangedSlideOffset(ap, furnitureData) {
    const runs = this.componentFreeRuns(ap, furnitureData, this.componentDragSubtree);
    const picked = this.snapSystem?.pickedRun;
    let t = 0;
    if (picked?.ap === ap) {
      t = picked.t;
    } else {
      this._slidingPlane.set(this._slidingPlaneNormal, -this._componentSnapPos.y);
      if (this.raycaster.ray.intersectPlane(this._slidingPlane, this._slidingIntersection)) {
        this._slidingLocalCursor.copy(this._slidingIntersection);
        ap.parentFurniture.getMesh().worldToLocal(this._slidingLocalCursor);
        t = ap.rangeParam(this._slidingLocalCursor);
      }
      t = clampToRuns(t, runs, ap.range);
    }
    ap.setActiveRunParam(t);
    return ap.rangePoint(t, this._slideOffset);
  },

  // Pose at `slideLocal` (null keeps `outPos`): connection offset rides the AP facing, then componentRotationY.
  _componentPoseAt(ap, furnitureData, slideLocal, outPos, outQuat) {
    const parentMesh = ap.parentFurniture.getMesh();
    ap.getAttachmentOrientation(outQuat);
    if (slideLocal) {
      const connOffset = this._resolveConnectionOffset(ap, furnitureData);
      parentMesh.localToWorld(outPos.copy(slideLocal));
      if (connOffset) outPos.add(_runOffset.set(connOffset.x, connOffset.y, connOffset.z).applyQuaternion(outQuat));
    }
    if (ap.componentRotationY !== undefined) {
      parentMesh.getWorldQuaternion(outQuat);
      outQuat.multiply(_scratchQuat.setFromAxisAngle(_Y_AXIS, ap.componentRotationY));
    }
  },

  /** Free `[{min, max, mid}]` runs for the dragged piece (Unity's RefreshActiveAnchorDisplays); cached per generation. */
  componentFreeRuns(ap, furnitureData, exclude = null) {
    const furnitureManager = this.app.getFurnitureManager?.();
    const ghost = furnitureManager?.getGhostFurniture?.();
    const box = ghost?.getLocalCollisionBox?.();
    if (!ap.range || !box?.size) return null;

    const generation = furnitureManager.attachmentGeneration ?? 0;
    const cached = ap._runCache;
    if (cached && cached.generation === generation && cached.data === furnitureData &&
        cached.exclude === exclude && cached.box === box) {
      return cached.runs;
    }

    const parent = ap.parentFurniture;
    _runAncestors.clear();
    for (let a = parent; a; a = a.attachedTo?.parentFurniture ?? null) _runAncestors.add(a);
    _runScale.copy(ghost.getMesh().scale);
    const meshes = this._collectModuleMeshes();

    const { min, max } = this._seatableSpan(ap, furnitureData, box);
    const steps = max > min ? Math.max(1, Math.ceil((max - min) / RANGE_SAMPLE_STEP)) : 0;
    const runs = [];
    let start = null;
    let last = min;
    for (let i = 0; i <= steps; i++) {
      const t = steps ? min + (max - min) * i / steps : min;
      this._componentPoseAt(ap, furnitureData, ap.rangePoint(t, _runSlide), _runPos, _runQuat);
      setComponentObb(_obbA, _pieceMatrix.compose(_runPos, _runQuat, _runScale), box);
      const free = !this._componentObbBlocked(_obbA, furnitureData, parent, ap, _runAncestors, exclude) &&
        !this._lacksSupport(_pieceMatrix, box, parent, meshes);
      if (free && start === null) start = t;
      if (!free && start !== null) {
        runs.push(freeRun(start, last));
        start = null;
      }
      last = t;
    }
    if (start !== null) runs.push(freeRun(start, max));

    ap._runCache = { generation, data: furnitureData, exclude, box, runs };
    return runs;
  },

  /** Range span the piece may take within RANGE_OVERHANG of the ends, else the middle only; expects _runScale set. */
  _seatableSpan(ap, furnitureData, box) {
    const { min, max } = ap.range;
    const parentMesh = ap.parentFurniture.getMesh();
    this._componentPoseAt(ap, furnitureData, ap.rangePoint(0, _runSlide), _runPos, _runQuat);
    _runAxis.copy(ap.rangeAxis).applyQuaternion(parentMesh.getWorldQuaternion(_runAxisQuat));
    const half = (
      Math.abs(_boxAxis.set(1, 0, 0).applyQuaternion(_runQuat).dot(_runAxis)) * box.size.x * Math.abs(_runScale.x) +
      Math.abs(_boxAxis.set(0, 0, 1).applyQuaternion(_runQuat).dot(_runAxis)) * box.size.z * Math.abs(_runScale.z)
    ) / 2;
    // Where the box's centre sits along the range, relative to the AP point it is seated at.
    _runOffset.copy(box.localCenterOffset).multiply(_runScale).applyQuaternion(_runQuat).add(_runPos);
    const centre = _runOffset.sub(parentMesh.localToWorld(ap.rangePoint(0, _runSlide))).dot(_runAxis);
    const lo = Math.max(min, min - RANGE_OVERHANG + half - centre);
    const hi = Math.min(max, max + RANGE_OVERHANG - half - centre);
    if (lo <= hi) return { min: lo, max: hi };
    const middle = THREE.MathUtils.clamp((min + max) / 2 - centre, min, max);
    return { min: middle, max: middle };
  },

  /** Reset all component drag tracking state. */
  resetComponentDragState() {
    if (this.componentDragTarget) this.componentDragTarget.setHighlight(false);
    this.componentDragOriginalAP = null;
    this.componentDragOriginalLocalPos = null;
    this.componentDragSubtree = null;
    this.componentDragOriginalPos = null;
    this.componentDragOriginalQuat = null;
    this.componentDragTarget = null;
    this.componentDragSlidOffset = null;
    if (this.snapSystem) this.snapSystem.pickedRun = null;
    this.isDragging = false;
    this.isDraggingValid = false;
    this.justFinishedDragging = true;
    this.mouseDownPosition = null;
    this.initialDragPositions = [];
    this.app.getCameraManager().getControls().enabled = true;
    this.clearAllAttachmentHighlights();
  },

  // Whether a ghost or placed component overlaps another, excluding its own ancestors and descendants.
  checkComponentCollision(item, parentFurniture, excludeFurniture = null, attachmentPoint = null) {
    if (!item || !parentFurniture) return false;

    const ancestors = this._ancestorSet;
    ancestors.clear();
    for (let a = parentFurniture; a; a = a.attachedTo?.parentFurniture ?? null) ancestors.add(a);
    const descendants = !excludeFurniture ? null
      : this.componentDragSubtree?.has(excludeFurniture) ? this.componentDragSubtree
      : collectSubtree(excludeFurniture);

    // A ghost standing in for a re-dragged component also carries that component's cushions.
    // One box around the lot would hold a carried cushion to its backrest's rules, so each
    // piece is tested by its own box and rules, at the pose the move gives it.
    if (excludeFurniture && item !== excludeFurniture) {
      const live = excludeFurniture.getMesh();
      live.updateWorldMatrix(true, true);
      item.getMesh().updateWorldMatrix(true, false);
      _moveMatrix.copy(live.matrixWorld).invert().premultiply(item.getMesh().matrixWorld);
      for (const piece of descendants) {
        const isRoot = piece === excludeFurniture;
        const pieceParent = isRoot ? parentFurniture : piece.attachedTo?.parentFurniture;
        const box = piece.boundingBox;
        if (!pieceParent || !box?.size || box.size.lengthSq() === 0) continue;
        setComponentObb(_obbA, _pieceMatrix.multiplyMatrices(_moveMatrix, piece.getMesh().matrixWorld), box);
        if (this._componentObbBlocked(_obbA, piece.furnitureData, pieceParent,
          isRoot ? attachmentPoint : piece.attachedTo, ancestors, descendants)) return true;
        if (isRoot && attachmentPoint?.range &&
            this._lacksSupport(_pieceMatrix, box, parentFurniture)) return true;
      }
      return false;
    }

    const box = item.getLocalCollisionBox?.() ?? item.boundingBox;
    if (!box?.size || box.size.lengthSq() === 0) return false;
    item.getMesh().updateWorldMatrix(true, false);
    setComponentObb(_obbA, item.getMesh().matrixWorld, box);
    if (this._componentObbBlocked(_obbA, item.furnitureData, parentFurniture, attachmentPoint, ancestors, descendants)) return true;
    return !!attachmentPoint?.range &&
      this._lacksSupport(item.getMesh().matrixWorld, box, parentFurniture);
  },

  /** The drawn meshes of every module, world matrices current and inverted; components excluded. */
  _collectModuleMeshes() {
    _moduleMeshes.length = 0;
    _moduleOwners.length = 0;
    for (const other of this.app.getFurnitureManager()?.getAllFurniture() || []) {
      if (other.furnitureData?.componentType !== 'module') continue;
      const root = other.getMesh();
      if (!root?.visible) continue;
      root.updateWorldMatrix(true, true);
      const stack = [root];
      while (stack.length) {
        const o = stack.pop();
        if (!o.visible) continue;
        const owner = o.userData?.furnitureElement;
        if (o !== root && owner && owner !== other) continue;
        if (o.isMesh && o.geometry?.attributes?.position) {
          const i = _moduleMeshes.push(o) - 1;
          _moduleOwners.push(other);
          (_moduleInverses[i] ??= new THREE.Matrix4()).copy(o.matrixWorld).invert();
        }
        for (const child of o.children) stack.push(child);
      }
    }
    return _moduleMeshes;
  },

  /** Unity's RaycastForCushionSupport; rays, since leaning cushion boxes dip into seats. Own module = first up. */
  _lacksSupport(matrix, box, host, meshes = this._collectModuleMeshes()) {
    _seatChain.clear();
    for (let a = host; a; a = a.attachedTo?.parentFurniture ?? null) {
      _seatChain.add(a);
      if (a.furnitureData?.componentType === 'module') break;
    }
    const c = box.localCenterOffset, s = box.size;
    const underside = _probe.set(c.x, c.y - s.y / 2, c.z).applyMatrix4(matrix).y;
    const reach = s.x / 2 - SUPPORT_END_INSET;
    for (const k of SUPPORT_PROBES) {
      _probe.set(c.x + k * reach, c.y + s.y / 2, c.z).applyMatrix4(matrix);
      _probeRay.set(_probe, _DOWN);
      _probeRay.origin.y += 2;
      let supported = false;
      for (let i = 0; i < meshes.length; i++) {
        const mesh = meshes[i];
        _localRay.copy(_probeRay).applyMatrix4(_moduleInverses[i]);
        const hit = meshBvh(mesh.geometry).raycastFirst(_localRay, THREE.DoubleSide);
        if (!hit) continue;
        supported = true;
        if (!_seatChain.has(_moduleOwners[i]) &&
            hit.point.applyMatrix4(mesh.matrixWorld).y > underside + SUPPORT_RISE_TOLERANCE) return true;
      }
      if (k === 0 && !supported) return true;
    }
    return false;
  },

  _componentObbBlocked(obb, itemData, parentFurniture, attachmentPoint, ancestors, descendants) {
    const isCushion = CUSHION_LIKE_COMPONENT_TYPES.has(itemData?.componentType);
    const itemUrlCode = itemData?.urlCode;
    // Backrests wrapping a module (matacao3-st's U) meet in its corners by design; an H75's box runs 25 cm into the seat.
    const wrapSide = itemData?.componentType === 'panel' && parentFurniture.furnitureData?.properties?.maxPanelSides
      ? attachmentPoint?.side : null;
    for (const other of this.app.getFurnitureManager()?.getAllFurniture() || []) {
      if (ancestors.has(other) || descendants?.has(other)) continue;
      if (other.furnitureData?.componentType === 'module') continue;
      const ob = other.boundingBox;
      if (!ob?.size || !ob.localCenterOffset) continue;

      // A cushion rests against backrests by design — overhanging its neighbours', clipping a
      // perpendicular one at an L-corner — whichever of the two is moved; only cushions block cushions.
      if (isCushion !== CUSHION_LIKE_COMPONENT_TYPES.has(other.furnitureData?.componentType)) continue;

      // A fixed slot nestles into its declared flanking cushions (diagon-mp/sp ↔ diagon-bp).
      if (nestlesWith(attachmentPoint, other.furnitureData?.urlCode) ||
          nestlesWith(other.attachedTo, itemUrlCode)) continue;

      const sameParent = other.attachedTo?.parentFurniture === parentFurniture;
      if (wrapSide && sameParent && other.furnitureData?.componentType === 'panel' && other.attachedTo.side !== wrapSide) continue;

      const pairTolerance = sameParent
        ? COMPONENT_COLLISION_TOLERANCE_SAME_PARENT
        : COMPONENT_COLLISION_TOLERANCE;
      const t = isCushion ? Math.max(pairTolerance, CUSHION_PRESS_TOLERANCE) : pairTolerance;

      // Live transform, not boundingBox.min/max: that goes stale and inflates 45° boxes.
      const otherMesh = other.getMesh();
      otherMesh.updateWorldMatrix(true, false);
      setComponentObb(_obbB, otherMesh.matrixWorld, ob);
      if (componentObbsOverlap(obb, _obbB, t)) return true;
    }
    return false;
  },

  _beginComponentDrag(selectedFurniture) {
    // Component drag: save original state, detach, create ghost
    this.componentDragOriginalAP = selectedFurniture.attachedTo;
    // detachFrom() resets the AP's slide, so keep it for a cancelled/invalid drop.
    this.componentDragOriginalLocalPos = selectedFurniture.attachedTo.slideOf(selectedFurniture).clone();
    this.componentDragSubtree = collectSubtree(selectedFurniture);
    this.componentDragOriginalPos = new THREE.Vector3();
    this.componentDragOriginalQuat = new THREE.Quaternion();
    selectedFurniture.getMesh().getWorldPosition(this.componentDragOriginalPos);
    selectedFurniture.getMesh().getWorldQuaternion(this.componentDragOriginalQuat);

    selectedFurniture.detachFrom(this.app.getScene());
    selectedFurniture.getMesh().visible = false;
    this.createDragGhost(selectedFurniture);
  },

  _moveComponent(selectedFurniture, raycaster, furnitureManager) {
    const componentType = selectedFurniture.furnitureData?.componentType;

    const cursorPos = this._cursorHit(raycaster, true);
    if (!cursorPos) return;

    const prevTarget = this.componentDragTarget;
    const closestAP = this._resolveComponentDragTarget(selectedFurniture, cursorPos);
    if (prevTarget && prevTarget !== closestAP) prevTarget.setHighlight(false);

    if (closestAP && this.dragGhostFurniture) {
      const slideLocal = this._resolveComponentSnapPose(closestAP, selectedFurniture.furnitureData);
      this.componentDragSlidOffset = slideLocal ? this._redragSlidOffset.copy(slideLocal) : null;

      this.dragGhostFurniture.setPosition(this._componentSnapPos.x, this._componentSnapPos.y, this._componentSnapPos.z);
      this.dragGhostFurniture.getMesh().quaternion.copy(this._componentSnapQuat);

      const isValid = !this.checkComponentCollision(
        this.dragGhostFurniture, closestAP.parentFurniture, selectedFurniture, closestAP
      );
      this.dragGhostFurniture.setSnapped(true, closestAP, null, isValid);
      this.dragGhostFurniture.show();
      closestAP.setHighlight(true, isValid);
      this.componentDragTarget = closestAP;
    } else if (this.dragGhostFurniture) {
      this.dragGhostFurniture.setPosition(cursorPos.x, cursorPos.y, cursorPos.z);
      this.dragGhostFurniture.setSnapped(false, null, null, false);
      this.dragGhostFurniture.show();
    }
    furnitureManager.updateSelectionOutline();
  },

  _dropComponent(selectedFurniture) {
    const dropAP = this.componentDragTarget;
    const originalAP = this.componentDragOriginalAP;
    let dropValid = dropAP && !dropAP.isOccupied;

    // Check panel attachment rules (same gate as snap system + AP visibility)
    if (dropValid && selectedFurniture.furnitureData?.componentType === "panel") {
      dropValid = dropAP.parentFurniture.canAttachPanel(dropAP, "panel");
    }

    // Moving to another AP must satisfy the same rules a fresh placement there would.
    if (dropValid && dropAP !== originalAP) {
      dropValid = dropAP.parentFurniture.shouldShowAttachmentPoint(dropAP);
    }

    // Check collision before clearing ghost
    if (dropValid && this.dragGhostFurniture) {
      dropValid = !this.checkComponentCollision(
        this.dragGhostFurniture, dropAP.parentFurniture, selectedFurniture, dropAP
      );
    }

    this.clearDragGhost();

    if (dropValid) {
      const data = selectedFurniture.furnitureData;
      const mesh = selectedFurniture.getMesh();
      const slid = this.componentDragSlidOffset;
      if (slid) this._componentPoseAt(dropAP, data, slid, mesh.position, _dropQuat);
      else dropAP.getWorldPositionWithOffset(this._resolveConnectionOffset(dropAP, data), mesh.position);
      dropAP.getAttachmentOrientation(mesh.quaternion);
      mesh.visible = true;
      selectedFurniture.attachTo(dropAP);
      if (slid) dropAP.setSlide(selectedFurniture, slid);
      selectedFurniture.calculateBoundingBox();

      // Leaving a slot can strand a flanked pillow (diagon-mp/sp) — same rule as deleting it.
      if (dropAP !== originalAP) {
        const oldModule = originalAP.parentFurniture.getNearestModule();
        if (oldModule) this.app.getFurnitureManager()?.removeUnsupportedFragileCushions(oldModule.getAllGroupModules());
      }
    } else {
      this.restoreComponentToOriginal(selectedFurniture);
    }

    this.resetComponentDragState();
    this.app.notifyStateChanged();

    if (selectedFurniture.isSelected) {
      this.app.getUIManager()?.updateSelection(
        this.app.getFurnitureManager()?.getSelectedFurnitureArray() || []
      );
    }
    this.app.getFurnitureManager()?.updateSelectionOutline();
  },
};
